function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) throw new Error(`Invalid hex color: ${hex}`);
  const r = parseInt(m[1]!, 16) / 255;
  const g = parseInt(m[2]!, 16) / 255;
  const b = parseInt(m[3]!, 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hsl(h: number, s: number, l: number): string {
  const round = (n: number) => Math.round(n * 100) / 100;
  return `hsl(${round(h)} ${round(s)}% ${round(l)}%)`;
}

/**
 * Generate an 11-stop solid color scale from a single accent hex.
 * Stops 0..10 go from very light to very dark with constant hue.
 */
export function deriveAccentScale(hex: string): readonly string[] {
  const { h, s } = hexToHsl(hex);
  const lightnessStops = [97, 93, 86, 76, 64, 50, 42, 34, 26, 18, 10];
  return lightnessStops.map((l) => hsl(h, s, l));
}

export const colors = {
  light: {
    bg: "hsl(0 0% 100%)",
    "bg-elevated": "hsl(0 0% 100%)",
    "bg-subtle": "hsl(220 14% 97%)",
    fg: "hsl(220 26% 14%)",
    "fg-muted": "hsl(220 9% 38%)",
    muted: "hsl(220 14% 96%)",
    border: "hsl(220 13% 91%)",
    "border-strong": "hsl(220 13% 78%)",
    accent: "hsl(217 91% 50%)",
    "accent-fg": "hsl(0 0% 100%)",
    "accent-strong": "hsl(217 91% 38%)",
    danger: "hsl(0 72% 51%)",
    "danger-fg": "hsl(0 0% 100%)",
    success: "hsl(142 71% 30%)",
    "success-fg": "hsl(0 0% 100%)",
    warning: "hsl(38 92% 50%)",
    "warning-fg": "hsl(38 92% 10%)",
    ring: "hsl(217 91% 50%)",
  },
  dark: {
    bg: "hsl(220 26% 8%)",
    "bg-elevated": "hsl(220 23% 11%)",
    "bg-subtle": "hsl(220 23% 13%)",
    fg: "hsl(220 14% 96%)",
    "fg-muted": "hsl(220 9% 64%)",
    muted: "hsl(220 23% 16%)",
    border: "hsl(220 13% 22%)",
    "border-strong": "hsl(220 13% 32%)",
    accent: "hsl(217 91% 65%)",
    "accent-fg": "hsl(220 26% 8%)",
    "accent-strong": "hsl(217 91% 78%)",
    danger: "hsl(0 72% 60%)",
    "danger-fg": "hsl(220 26% 8%)",
    success: "hsl(142 71% 50%)",
    "success-fg": "hsl(220 26% 8%)",
    warning: "hsl(38 92% 55%)",
    "warning-fg": "hsl(220 26% 8%)",
    ring: "hsl(217 91% 65%)",
  },
} as const;

export type ColorTokens = typeof colors.light;
